// Human-readable explanations for the Roles & Permissions matrix.
//
// `MODULE_GUIDE` describes what each card (the module prefix before the first
// dot, e.g. "Documents") references — the page/surface or service it governs.
// `PERMISSION_GUIDE` describes what each individual `Module.Action` permission
// directly controls or grants.
//
// Source of truth is the backend catalog `PhoenixCapital.Core.Constants.Permissions`
// (and its XML-doc comments). When a permission is added there, add its entry
// here so the in-app explainer stays complete.

export const MODULE_GUIDE: Record<string, string> = {
  System:
    "Platform-wide configuration. Backs the Admin settings surfaces (brand settings, audit log viewer). System.Configure is also the gate that unlocks editing on this Roles & Permissions page.",
  Users:
    "Staff user directory (Admin → Users) and UserService — controls who can create, view, edit, and remove staff (ApplicationUser) accounts.",
  Documents:
    "Document Management System (DMS) — the folder tree, file CRUD, archive state, bulk export, admin folder templates, and typed document links.",
  Brokers:
    "Broker CRM records (/brokers) — the broker master directory, qualification, and the status lifecycle including the stricter disqualify transition.",
  Escrow:
    "Escrow.com integration (Tools nav) — API-driven escrow transactions: view, draft, and approve (approval fires the Escrow.com create call).",
  Brokerages:
    "Brokerage firm records — the covering-firm directory that brokers belong to.",
  Leads:
    "Broker CRM lead pipeline — lead CRUD, conversion into broker records, and bulk import.",
  Territories:
    "Broker CRM territory assignments — the geographic routing table.",
  CallLogs:
    "Broker CRM call logging — outbound call records against leads and brokers.",
  Tasks:
    "Platform-wide WorkTask system — personal and assigned tasks, cross-user assignment, full-org visibility, and the TaskTag lookup.",
  Notes:
    "Internal notes moderation — the Compliance surface for seeing Compliance-deleted notes and removing any note. (Posting a note needs no permission.)",
  Deals:
    "Deal Pipeline records — deal CRUD plus the workflow gates (assign, advance stage, decline, withdraw, certify, archive/restore).",
  DocumentRequests:
    "Pre-Approval document request lifecycle — the per-deal requests sent to and received from counterparties.",
  DocumentRequestTemplates:
    "Admin catalog of Pre-Approval document request templates (the DocumentTypeRef master list). Edits affect new deals only.",
  Persons:
    "Counterparty master records — individual Person records shared across deals.",
  Entities:
    "Counterparty master records — legal-entity records shared across deals.",
  Businesses:
    "Counterparty master records — operating-business records shared across deals.",
  DealFlow:
    "Deal Flow detail page (the underwriting → closing workflow) — the 7-pill assignment roster, criteria checklist actions, lane assessments, failures/escalations, escrow, closing, metrics, the DQR catalog, the decline workflow, pre-approval, and admin undo.",
  Reunderwriting:
    "Reunderwriting module — full-credit-review reevaluation events on seasoned loans. Mirrors the Deal Flow workflow gates.",
  Loans:
    "Portfolio Administration loan record (/portfolio-admin/loans) — loan read/manage, the review lifecycle, and admin authoring of the Setup / Profile / QA catalogs.",
  LoanTape:
    "Loan Tape monthly close (/portfolio-admin/loan-tape) — open/enter/lock/unlock the monthly tape and the PSA CSV export.",
  Failure:
    "Failure Tracking aggregator (now dormant) — manual read/resolve of LoanFailure rows kept for edge cases.",
  Metrics:
    "Portfolio Administration metrics dashboard — read and CSV export.",
  SegmentLedger:
    "Per-segment principal ledger — read and manual adjustments.",
  Vendors:
    "Vendor records — the vendor directory, profile edits, and the collapsed Manage gate (create / status / waive / portal-invite).",
  VendorBilling:
    "Vendor Fee Income (VFI) invoices — the staff ledger/dashboard plus the issue / void / record-manual-payment mutations.",
  UCC:
    "UCC filing & lien search (loan servicing) — view filings and file initial / continuation / termination / amendment, plus order searches.",
  Insurance:
    "Loan Insurance tab — view policies and claims, and verify coverage.",
  Enforcer:
    "Local Enforcer registry — view/manage enforcers plus the engagement lifecycle and cost-approval threshold.",
  Waterfall:
    "Waterfall configuration & execution — edit/approve config and run/approve/reverse the deterministic engine.",
  Defaults:
    "Defaults & Workout cockpit — read the default-case queue and manage the workflow.",
  Default:
    "Default workflow operations — the heavier case transitions: legal action, DACA activation, and charge-off / write-off.",
  Workout:
    "Workout ledger — record costs and recoveries and approve recoveries.",
  Spv:
    "SPV trust entity (Delaware statutory trust) — read and manage the trust record.",
  SpvPool:
    "SPV pool — composition build, contribution lock, IDUP, status advance, close, and the atomic formation ceremony.",
  RightSize:
    "Right-Size workflow — preview and execute the retire-and-substitute.",
  Payoff:
    "Loan payoff run — settle arrears + fees + prepayment premium, return remaining principal to the seller, and retire the loan.",
  Resale:
    "Phoenix-Backed Resale workflow — initiate, mature the resale bridge, and run the financial closing.",
  IndentureTrustee:
    "Indenture Trustee service-provider records — read and manage.",
  TrustCompany: "Trust Company service-provider records — read and manage.",
  PlacementAgent: "Placement Agent service-provider records — read and manage.",
  BackupServicer: "Backup Servicer service-provider records — read and manage.",
  Investor:
    "Investor master records (SPV Pool) — read/manage, plus the sensitive-fields reveal (plaintext tax ID / banking instructions).",
  Investors:
    "Investor Reporting module — read access to the legacy investor-reporting surface.",
  Reports:
    "LLM Reports generator — generate / read / download reports, manage templates, and the v3.2 AI-assistant, budget, and finalization gates.",
  Compliance:
    "Loan Compliance module — the cross-track exception log, the annual trustee package sign-off, legal/financial ops checks, the material-event register, and the annual legal review.",
  Claims:
    "Commission claims — the broker commission claim lifecycle (create / submit / revise / review / agree / contest / confirm revenue).",
  Payments:
    "Servicing payments — record / read / adjust payments and the cross-payment cascade reverse.",
  Closings:
    "Closing reversal (Payment System) — the larger-blast-radius reversal of a deal closing.",
  Events:
    "Loan-event reversal (Payment System) — reversing a structural loan event.",
  PIR: "Payment Instruction Reports — generate a PIR and mark it instructed.",
  ServicerInstruction:
    "Servicer Instruction Reports — generate the servicer-channel disbursement batch (the inverse of the PIR).",
  Pools: "Loan pool management — the administrative Pools.Manage gate.",
  Commission:
    "Commission module operations — escalation, period lock, reports, rate/allocation admin, payouts, and manual claim creation.",
  Messaging:
    "Internal direct messaging — send DMs and the compliance read-all.",
  Lookups:
    "Admin-managed lookups — write access to the Role and Category taxonomy tables.",
  CertificationDefinitions:
    "Certification definition catalog — admin CRUD on the cross-entity CertificationDefinition master.",
  Certifications:
    "Per-entity certifications surface — read access. (Certify/revoke are gated per-definition, not here.)",
  Communications:
    "Communication Log — log entries, the compliance read-all, and the admin CSV export.",
  KnowledgeBase:
    "Knowledge Base — the Ask chat, the Browse Library folder grid, and admin document management.",
  Collection:
    "External Intake & Collection — package management, sending, item review, templates, and the staging review / scan-override seam.",
  DealSubmission:
    "Broker New Deal Submission — template authoring, staging review/import, and the scan override.",
  Broker:
    "Broker public application approval — the approve action on a BrokerApplication (distinct from the Brokers CRM directory).",
  PortalMaterials:
    "Portal Materials library — admin authoring of the external-portal download library.",
  Portal:
    "External portal accounts registry — staff management of PortalUser (buyer/seller/vendor/broker) accounts.",
  BackgroundCheck:
    "Baselayer Background Checks (Tools nav) — read, the per-type run gates (KYB / consumer / online / lien), and UCC draft/approve.",
  FinancialReporting:
    "Financial Reporting suite — the reviewer second-signature (certify + file) and the admin series-catalog activation gate.",
};

export const PERMISSION_GUIDE: Record<string, string> = {
  // System
  "System.Configure":
    "Edit platform configuration; also the gate that unlocks editing on this Roles & Permissions page.",
  "System.AuditView": "Open the system-wide audit log viewer.",
  "System.ManageBrandSettings":
    "Edit brand settings (logo, colors, product name).",

  // Users
  "Users.Create": "Create new staff user accounts.",
  "Users.Read": "View the staff user directory and user detail.",
  "Users.Update":
    "Edit staff user accounts — roles, profile, and password reset.",
  "Users.Delete": "Deactivate / remove staff user accounts.",

  // Documents
  "Documents.Create": "Upload new documents.",
  "Documents.Read": "View and download documents.",
  "Documents.Update": "Rename, move, and edit document metadata.",
  "Documents.Delete": "Delete documents.",
  "Documents.CreateFolder": "Create folders in the DMS tree.",
  "Documents.MoveFolder": "Move / reparent folders.",
  "Documents.DeleteFolder": "Delete folders.",
  "Documents.BulkExport": "Bulk-export documents as a ZIP.",
  "Documents.ManageTemplates": "Manage admin folder-structure templates.",
  "Documents.Archive": "Archive documents (soft, reversible).",
  "Documents.Unarchive": "Restore archived documents.",
  "Documents.ViewArchived": "See archived documents in listings.",
  "Documents.Link": "Create / remove typed relationships between documents.",

  // Brokers
  "Brokers.Create": "Create broker records.",
  "Brokers.Read": "View broker records.",
  "Brokers.Update": "Edit broker records and drive the basic status changes.",
  "Brokers.Delete": "Delete broker records.",
  "Brokers.Qualify": "Run the broker qualification check.",
  "Brokers.Disqualify":
    "Apply the stricter Disqualified status (and reactivate from it).",

  // Escrow
  "Escrow.Read": "View the escrow transaction list and detail.",
  "Escrow.Draft": "Create and edit a draft escrow transaction.",
  "Escrow.Approve":
    "Approve a draft, firing the Escrow.com create call and archiving it.",

  // Brokerages
  "Brokerages.Read": "View brokerage firm records.",
  "Brokerages.Create": "Create brokerage firm records.",
  "Brokerages.Update": "Edit brokerage firm records.",
  "Brokerages.Delete": "Delete brokerage firm records.",
  "Brokerages.Archive": "Archive brokerage firm records.",

  // Leads
  "Leads.Create": "Create leads.",
  "Leads.Read": "View leads.",
  "Leads.Update": "Edit leads.",
  "Leads.Delete": "Delete leads.",
  "Leads.Convert": "Convert a lead into a broker record.",
  "Leads.Import": "Bulk-import leads.",

  // Territories
  "Territories.Read": "View territory assignments.",
  "Territories.Manage": "Edit the territory assignment table.",

  // CallLogs
  "CallLogs.Create": "Log a call against a lead or broker.",
  "CallLogs.Read": "View call-log history.",

  // Tasks
  "Tasks.Create": "Create tasks.",
  "Tasks.Read": "View tasks.",
  "Tasks.Update": "Edit tasks.",
  "Tasks.Delete": "Delete tasks.",
  "Tasks.AssignToOthers": "Assign tasks to other users.",
  "Tasks.ViewAll": "See all tasks org-wide, not just your own.",
  "Tasks.ManageTags": "Manage the TaskTag lookup.",

  // Notes
  "Notes.Moderate":
    "See Compliance-deleted notes and delete any note with a reason.",

  // Deals
  "Deals.Create": "Create deals.",
  "Deals.Read": "View deals.",
  "Deals.Update": "Edit deals.",
  "Deals.Delete": "Delete deals.",
  "Deals.Assign": "Assign a deal to a team member.",
  "Deals.Reassign": "Reassign a deal to a different owner.",
  "Deals.AdvanceStage": "Move a deal to the next pipeline stage.",
  "Deals.Decline": "Decline a deal.",
  "Deals.Withdraw": "Withdraw a deal.",
  "Deals.CertifyForUW": "Certify a deal as ready for underwriting.",
  "Deals.Archive": "Archive a deal.",
  "Deals.Restore": "Restore an archived deal.",

  // DocumentRequests
  "DocumentRequests.Create": "Create a pre-approval document request.",
  "DocumentRequests.Read": "View document requests.",
  "DocumentRequests.Update": "Edit a document request.",
  "DocumentRequests.Send": "Send a request to the counterparty.",
  "DocumentRequests.Receive": "Mark a request's documents as received.",
  "DocumentRequests.Approve": "Approve a received document.",
  "DocumentRequests.Reject": "Reject a received document.",
  "DocumentRequests.Waive": "Waive a required document.",

  // DocumentRequestTemplates
  "DocumentRequestTemplates.Manage":
    "Admin CRUD on the document-request template catalog.",

  // Persons
  "Persons.Create": "Create Person counterparty records.",
  "Persons.Read": "View Person counterparty records.",
  "Persons.Update": "Edit Person counterparty records.",
  "Persons.Delete": "Delete Person counterparty records.",

  // Entities
  "Entities.Create": "Create legal-entity counterparty records.",
  "Entities.Read": "View legal-entity counterparty records.",
  "Entities.Update": "Edit legal-entity counterparty records.",
  "Entities.Delete": "Delete legal-entity counterparty records.",

  // Businesses
  "Businesses.Create": "Create operating-business counterparty records.",
  "Businesses.Read": "View operating-business counterparty records.",
  "Businesses.Update": "Edit operating-business counterparty records.",
  "Businesses.Delete": "Delete operating-business counterparty records.",

  // Loans
  "Loans.Read": "View loan records and the Portfolio Admin loan page.",
  "Loans.Update": "Edit basic loan fields.",
  "Loans.Manage":
    "Full loan management — most write actions across the loan tabs.",
  "Loans.SetupCertify": "Certify Setup checklist items.",
  "Loans.RecordReview": "Record a loan review (e.g. a DSCR certificate).",
  "Loans.CertifyReview":
    "Certify a recorded review on behalf of underwriting.",
  "Loans.WaiveReview": "Waive a failed review (special-servicing path).",
  "Loans.InitiateReview": "Send a loan to underwriter review (Tier 2).",
  "Loans.ManageSetupCatalog": "Admin-author the LS Setup catalog.",
  "Loans.ManageProfileFields":
    "Admin-author the Loan Administration profile field definitions.",
  "Loans.ManageQaCatalog": "Admin-author the QA review scope-area catalog.",

  // LoanTape
  "LoanTape.Read": "View the loan tape.",
  "LoanTape.Enter": "Enter monthly-close data.",
  "LoanTape.Lock": "Lock a monthly close.",
  "LoanTape.Unlock": "Unlock a locked monthly close.",
  "LoanTape.Export": "Export the PSA loan-tape CSV (Exhibit B).",

  // Failure
  "Failure.Read": "View the failure-tracking aggregate.",
  "Failure.Resolve": "Manually resolve a LoanFailure.",

  // Metrics
  "Metrics.Read": "View the metrics dashboard.",
  "Metrics.Export": "Export metrics as CSV.",

  // SegmentLedger
  "SegmentLedger.Read": "View the per-segment principal ledger.",
  "SegmentLedger.ApplyAdjustment": "Apply a manual ledger adjustment.",

  // Vendors
  "Vendors.Read": "View vendor records.",
  "Vendors.Update": "Edit vendor profile fields.",
  "Vendors.Manage":
    "Create vendors, change status, waive requirements, and send portal invites.",

  // VendorBilling
  "VendorBilling.Read": "View the VFI invoice ledger / dashboard / drawer.",
  "VendorBilling.Issue": "Issue a VFI invoice.",
  "VendorBilling.Void": "Void a VFI invoice.",
  "VendorBilling.RecordManualPayment":
    "Record a manual (off-Stripe) invoice payment.",

  // UCC
  "UCC.Read": "View UCC filings and lien searches.",
  "UCC.FileInitial": "File a UCC-1 initial filing.",
  "UCC.FileContinuation": "File a UCC continuation.",
  "UCC.FileTermination": "File a UCC termination.",
  "UCC.FileAmendment": "File a UCC amendment.",
  "UCC.OrderSearch": "Order a UCC lien search.",

  // Insurance
  "Insurance.Read": "View insurance policies and claims.",
  "Insurance.Verify": "Verify insurance coverage.",

  // Enforcer
  "Enforcer.Read": "View the Local Enforcer registry.",
  "Enforcer.Manage": "Create / edit enforcer records.",
  "Enforcer.Engage": "Engage an enforcer on a loan.",
  "Enforcer.CostApprove": "Approve enforcer costs above the threshold.",

  // Waterfall
  "Waterfall.ConfigEdit": "Edit a waterfall configuration draft.",
  "Waterfall.ConfigApprove":
    "Approve a waterfall configuration (materializes its priorities).",
  "Waterfall.Run":
    "Retired (SP-6) — the manual waterfall-run engine surface was removed; the v3 servicing orchestrator is the only execution path.",
  "Waterfall.Approve":
    "Retired (SP-6) — no longer gates any endpoint.",
  "Waterfall.Reverse":
    "Retired (SP-6) — no longer gates any endpoint; reversals run through the v3 payment engine.",
  "Waterfall.SelfApprove":
    "Propose and approve the same config (separation-of-duty override).",

  // Defaults
  "Defaults.Read": "View the Defaults & Workout cockpit.",
  "Defaults.Manage": "Open / advance default cases and drive the workflow.",

  // Default
  "Default.LegalAction": "Take legal action on a default case.",
  "Default.DACAActivate": "Activate a DACA (deposit-account control).",
  "Default.WriteOff":
    "Charge off / write off a loan (acceleration & closed-final transitions).",

  // Workout
  "Workout.RecordCost": "Record a workout cost.",
  "Workout.RecordRecovery": "Record a workout recovery.",
  "Workout.ApproveRecovery": "Approve a recorded recovery.",

  // Spv
  "Spv.Read": "View SPV trust records.",
  "Spv.Manage": "Create / edit SPV trust records.",

  // SpvPool
  "SpvPool.Read": "View SPV pools.",
  "SpvPool.Build": "Edit pool composition.",
  "SpvPool.Contribute": "Lock contribution segments into a pool.",
  "SpvPool.IDUP": "Generate the IDUP.",
  "SpvPool.AdvanceStatus": "Advance the pool through its 9-state lifecycle.",
  "SpvPool.Close": "Run the pool closing checklist.",
  "SpvPool.Formation":
    "Run the atomic pool-formation ceremony (CSS filing, SSN issuance, RUF + SFTF).",

  // RightSize
  "RightSize.Initiate": "Preview and execute a Right-Size.",

  // Payoff
  "Payoff.Initiate": "Run a loan payoff.",

  // Resale
  "Resale.Initiate": "Open a Phoenix-Backed Resale.",
  "Resale.MatureBridge": "End the resale bridge period on the Spread Note.",
  "Resale.FinancialClosing":
    "Run the Spread Note pool's first investor closing.",

  // Service-provider counterparties
  "IndentureTrustee.Read": "View Indenture Trustee records.",
  "IndentureTrustee.Manage": "Create / edit / attach Indenture Trustees.",
  "TrustCompany.Read": "View Trust Company records.",
  "TrustCompany.Manage": "Create / edit / attach Trust Companies.",
  "PlacementAgent.Read": "View Placement Agent records.",
  "PlacementAgent.Manage": "Create / edit / attach Placement Agents.",
  "BackupServicer.Read": "View Backup Servicer records.",
  "BackupServicer.Manage": "Create / edit / attach Backup Servicers.",

  // Investor
  "Investor.Read": "View investor records (sensitive fields masked).",
  "Investor.Manage": "Create / edit investor records and run the dedup lookup.",
  "Investor.ViewSensitiveFields":
    "Reveal plaintext tax ID and banking instructions.",

  // Investors
  "Investors.Read": "View the Investor Reporting surface.",

  // Reports
  "Reports.Generate": "Generate a report from a template.",
  "Reports.Read": "View report submissions.",
  "Reports.Download": "Download generated report files.",
  "Reports.ManageTemplates":
    "Admin-author report templates (prompts + forms).",
  "Reports.DraftPrompts": "Use the AI 'Help me write this' prompt builder.",
  "Reports.UseAnswerAssistant": "Use the v3.2 answer assistant.",
  "Reports.GenerateLargeBudget":
    "Override the token-budget threshold on generation.",
  "Reports.RevertFinalization": "Un-finalize a finalized report (admin).",

  // Compliance
  "Compliance.Read": "View the compliance surfaces.",
  "Compliance.QaReview": "Conduct loan QA reviews.",
  "Compliance.Calendar": "Manage the compliance calendar.",
  "Compliance.ViewExceptions": "View the exception log.",
  "Compliance.ManageExceptions":
    "Open / update / reassign / remediate exceptions.",
  "Compliance.ResolveExceptions": "Cure and close exceptions.",
  "Compliance.WaiveExceptions": "Waive an exception (override).",
  "Compliance.SignTrusteePackage":
    "Sign the annual trustee compliance package.",
  "Compliance.RecordLegalOpsCheck":
    "Record legal-ops periodic checks (Identity / UCC / Insurance / DACA).",
  "Compliance.ManageMaterialEvents":
    "Capture / notice / dispose / escalate material events.",
  "Compliance.RecordFinancialOpsCheck":
    "Record financial-ops periodic checks (DSCR / Compliance Cert / Loan Tape).",
  "Compliance.ConductLegalReview":
    "Write the annual legal compliance review (engagement / scoping / memo).",
  "Compliance.FinalizeLegalReview": "Sign off / reissue a legal review.",
  "Compliance.AcceptLegalReview":
    "Supervisor acceptance of an outside-counsel legal review.",

  // Claims
  "Claims.Create": "Create a commission claim.",
  "Claims.Read": "View your commission claims.",
  "Claims.ReadAll": "View all commission claims.",
  "Claims.Submit": "Submit a claim.",
  "Claims.Revise": "Revise a claim.",
  "Claims.Withdraw": "Withdraw a claim.",
  "Claims.Review": "Review a submitted claim.",
  "Claims.Agree": "Agree to a claim.",
  "Claims.Contest": "Contest a claim.",
  "Claims.ConfirmRevenue": "Confirm revenue on a claim.",

  // Payments
  "Payments.Create": "Record a servicing payment.",
  "Payments.Read": "View your payments.",
  "Payments.ReadAll": "View all payments.",
  "Payments.Adjust": "Adjust a payment.",
  "Payments.Reverse": "Reverse a payment (cascade).",

  // Closings / Events (payment reversals)
  "Closings.Reverse": "Reverse a deal closing (large structural blast radius).",
  "Events.Reverse": "Reverse a structural loan event.",

  // PIR
  "PIR.Generate": "Generate a Payment Instruction Report.",
  "PIR.MarkInstructed": "Mark a PIR as instructed.",

  // ServicerInstruction
  "ServicerInstruction.Generate": "Generate a Servicer Instruction Report.",

  // Pools
  "Pools.Manage": "Administer loan pools.",

  // Commission
  "Commission.Escalate": "Escalate a commission dispute.",
  "Commission.ResolveEscalation": "Resolve a commission escalation.",
  "Commission.PeriodLock": "Lock a commission period.",
  "Commission.Reports": "View your commission reports.",
  "Commission.ReportsAll": "View all commission reports.",
  "Commission.ManageAllocations":
    "Edit the position × pool standing-rate allocation table.",
  "Commission.ManageRates": "Edit per-user commission rate overrides.",
  "Commission.Pay": "Record commission payouts.",
  "Commission.ManualCreate": "Manually create a commission claim from scratch.",

  // Messaging
  "Messaging.Send": "Send DMs and read your own conversations.",
  "Messaging.ViewAll": "Read any conversation (compliance oversight).",

  // Lookups
  "Lookups.ManageRoles": "Edit the Role taxonomy table.",
  "Lookups.ManageCategories": "Edit the Category taxonomy table.",

  // CertificationDefinitions
  "CertificationDefinitions.Read": "View certification definitions.",
  "CertificationDefinitions.Create": "Create a certification definition.",
  "CertificationDefinitions.Update": "Edit a certification definition.",
  "CertificationDefinitions.Deactivate":
    "Deactivate a certification definition.",

  // Certifications
  "Certifications.Read": "View per-entity certifications.",

  // Communications
  "Communications.Log": "Log a communication entry.",
  "Communications.ViewAll": "Read all communication entries (compliance).",
  "Communications.Export": "Export the communication log CSV.",

  // DealFlow
  "DealFlow.ViewDeal": "Open the Deal Flow detail page.",
  "DealFlow.AssignTeam": "Assign team members to the deal's pill roster.",
  "DealFlow.SelfClaimSlot": "Claim an open roster slot for yourself.",
  "DealFlow.MarkSlotNotApplicable": "Mark a roster slot not-applicable.",
  "DealFlow.WriteUnderwritingTab": "Edit the Underwriting tab.",
  "DealFlow.WriteAnalystTab": "Edit an analyst (A1–A4) tab.",
  "DealFlow.CertifyChecklistItem": "Certify a criteria checklist item.",
  "DealFlow.EscalateChecklistItem": "Escalate a checklist item.",
  "DealFlow.DeclineChecklistItem": "Decline a checklist item.",
  "DealFlow.WaiveChecklistItem": "Waive a checklist item.",
  "DealFlow.IssueLanePassFail": "Issue a lane pass/fail assessment.",
  "DealFlow.VerifyAnalystWork": "Verify completed analyst work.",
  "DealFlow.IssueApprovalLetter": "Issue the approval letter.",
  "DealFlow.AssignClosingCoordinator": "Assign the closing coordinator.",
  "DealFlow.WriteClosingTab": "Edit the Closing tab.",
  "DealFlow.ReleaseEscrow": "Release escrow.",
  "DealFlow.CreateLoan": "Create the loan at closing hand-off.",
  "DealFlow.ViewFailures": "View the deal failure-tracking surface.",
  "DealFlow.ResolveFailure": "Resolve a deal failure.",
  "DealFlow.ViewEscalations": "View escalations.",
  "DealFlow.ResolveEscalation": "Resolve an escalation.",
  "DealFlow.ViewMetrics": "View your deal-flow metrics.",
  "DealFlow.ViewAllMetrics": "View org-wide deal-flow metrics.",
  "DealFlow.OverrideGate": "Override a workflow gate.",
  // There is no reopen workflow: the permission only exempts its holder from
  // the closed-deal read-only lock (owner ruling, 2026-10-26).
  "DealFlow.ReopenClosedDeal": "Change a closed deal, which is read-only for everyone else.",
  "DealFlow.EditWithdrawnOrDeclinedDeal":
    "Change a withdrawn or declined deal, which is read-only for everyone else.",
  "DealFlow.Admin.Undo":
    "Undo locked-in deal-flow actions (hand-off, cert sign, finalize, lane assessment, criteria terminal).",
  "DealFlow.ManageCatalog":
    "Admin-edit the DQR criteria / certification catalog templates.",
  "DealFlow.ChangeCounterparty":
    "Change the borrower / seller / business on a pre-closing deal.",
  "DealFlow.ProposeDecline": "Propose a deal-level decline.",
  "DealFlow.WithdrawDeclineProposal": "Withdraw a pending decline proposal.",
  "DealFlow.ConfirmDecline": "Ratify a proposed decline as final.",
  "DealFlow.EscalateIntakeItem": "Escalate an intake checklist item.",
  "DealFlow.EscalateDocumentRequest":
    "Escalate a pre-approval document request.",
  "DealFlow.PreApproval.ManageApplications":
    "Manage pre-approval applications (the LA attestation surface).",
  "DealFlow.PreApproval.IssueLetters": "Issue the Conditional Approval letters.",
  "DealFlow.PreApproval.SubmitToUnderwriting":
    "Submit a pre-approval to underwriting.",

  // Reunderwriting
  "Reunderwriting.Initiate": "Launch a reunderwriting case from a loan.",
  "Reunderwriting.ViewCase": "Open a reunderwriting case.",
  "Reunderwriting.AssignTeam": "Assign the reunderwriting roster.",
  "Reunderwriting.SelfClaimSlot": "Claim an open reunderwriting slot.",
  "Reunderwriting.MarkSlotNotApplicable":
    "Mark a reunderwriting slot not-applicable.",
  "Reunderwriting.WriteUnderwritingTab":
    "Edit the reunderwriting Underwriting tab.",
  "Reunderwriting.WriteAnalystTab": "Edit a reunderwriting analyst tab.",
  "Reunderwriting.WriteClosingTab": "Edit the reunderwriting Closing tab.",
  "Reunderwriting.CertifyChecklistItem":
    "Certify a reunderwriting checklist item.",
  "Reunderwriting.EscalateChecklistItem":
    "Escalate a reunderwriting checklist item.",
  "Reunderwriting.DeclineChecklistItem":
    "Decline a reunderwriting checklist item.",
  "Reunderwriting.WaiveChecklistItem":
    "Waive a reunderwriting checklist item.",
  "Reunderwriting.IssueLanePassFail":
    "Issue a reunderwriting lane pass/fail.",
  "Reunderwriting.VerifyAnalystWork": "Verify reunderwriting analyst work.",
  "Reunderwriting.ViewFailures": "View reunderwriting failures.",
  "Reunderwriting.ResolveFailure": "Resolve a reunderwriting failure.",
  "Reunderwriting.ViewEscalations": "View reunderwriting escalations.",
  "Reunderwriting.ResolveEscalation": "Resolve a reunderwriting escalation.",
  "Reunderwriting.ViewMetrics": "View your reunderwriting metrics.",
  "Reunderwriting.ViewAllMetrics": "View org-wide reunderwriting metrics.",

  // KnowledgeBase
  "KnowledgeBase.Ask": "Use the KB chat (Ask).",
  "KnowledgeBase.BrowseLibrary": "Browse the KB folder library.",
  "KnowledgeBase.ManageDocuments":
    "Admin-upload / retire KB documents and edit the access matrix.",

  // Collection
  "Collection.ManagePackages": "Create / edit collection packages.",
  "Collection.ReadPackages": "View collection packages.",
  "Collection.SendPackages": "Send a package to recipients.",
  "Collection.ReviewItems": "Review submitted collection items.",
  "Collection.ManageTemplates": "Admin-author collection templates.",
  "Collection.Staging.ReviewAndFile":
    "Review the staging queue and approve-and-file a submission into the DMS.",
  "Collection.Staging.OverrideScan":
    "Manually mark a stuck recipient upload as scanned.",

  // DealSubmission
  "DealSubmission.ManageTemplates":
    "Admin-author deal-submission wizard templates.",
  "DealSubmission.Review":
    "Triage the deal staging queue (return-with-comments).",
  "DealSubmission.Import":
    "Approve & import a submission — creates the deal and files documents.",
  "DealSubmission.OverrideScan":
    "File a document whose virus scan did not return Clean.",

  // Broker
  "Broker.Applications.Approve": "Approve a public broker application.",

  // PortalMaterials
  "PortalMaterials.Manage":
    "Admin-author the portal materials download library.",

  // Portal
  "Portal.ManageAccounts":
    "Manage external portal accounts (disable / enable / archive).",

  // BackgroundCheck
  "BackgroundCheck.Read": "View background checks.",
  "BackgroundCheck.RunKyb": "Run a KYB business check.",
  "BackgroundCheck.RunConsumer": "Run a consumer check.",
  "BackgroundCheck.RunOnline": "Run an online-presence check.",
  "BackgroundCheck.RunLienSearch": "Run a lien search.",
  "BackgroundCheck.UccDraft": "Draft a Baselayer UCC filing.",
  "BackgroundCheck.UccApprove": "Approve a Baselayer UCC filing.",

  // FinancialReporting
  "FinancialReporting.Review":
    "Reviewer second-signature (certify + file) on a financial report.",
  "FinancialReporting.ManageSeriesCatalog":
    "Admin-flip report-series activation state.",
};
